/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
    onAuthStateChanged,
    signInWithRedirect,
    getRedirectResult,
    signOut,
    User as FirebaseUser
} from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { auth, googleProvider } from '../config/firebase';
import { User, UserRole, AuthContextType } from '../types/auth.types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// RBAC Permission Map (shared with Magnus-Core backend - see backend/auth/permissions.py)
const PERMISSION_MAP: Record<string, UserRole[]> = {
    // Resolve Permissions
    'resolve.read': ['viewer', 'engineer', 'projectManager', 'org_admin', 'clientAdmin', 'platform_admin', 'autoGainAdmin'],
    'resolve.write': ['engineer', 'projectManager', 'org_admin', 'clientAdmin', 'platform_admin', 'autoGainAdmin'],

    // Incident Management
    'incident.read': ['viewer', 'engineer', 'projectManager', 'org_admin', 'clientAdmin', 'platform_admin', 'autoGainAdmin'],
    'incident.write': ['engineer', 'projectManager', 'org_admin', 'clientAdmin', 'platform_admin', 'autoGainAdmin'],
    'incident.delete': ['org_admin', 'clientAdmin', 'platform_admin', 'autoGainAdmin'],

    // AI Features
    'ai.use_basic': ['viewer', 'engineer', 'projectManager', 'org_admin', 'clientAdmin', 'platform_admin', 'autoGainAdmin'],
    'ai.use_prime': ['engineer', 'projectManager', 'org_admin', 'clientAdmin', 'platform_admin', 'autoGainAdmin'],

    // Project access (shared across apps)
    'project.read': ['viewer', 'engineer', 'projectManager', 'org_admin', 'clientAdmin', 'platform_admin', 'autoGainAdmin'],
    'project.write': ['engineer', 'projectManager', 'org_admin', 'clientAdmin', 'platform_admin', 'autoGainAdmin'],
    'project.delete': ['projectManager', 'org_admin', 'clientAdmin', 'platform_admin', 'autoGainAdmin'],
};

// Builds a User object for an unapproved (pending) user
function buildPendingUser(fbUser: FirebaseUser): User {
    return {
        uid: fbUser.uid,
        email: fbUser.email || '',
        displayName: fbUser.displayName || 'User',
        photoURL: fbUser.photoURL || undefined,
        role: 'pending' as UserRole,
        isApproved: false,
        createdAt: new Date(),
        lastLogin: new Date(),
    };
}

// Derives permission list from role map + allowed apps
function buildPermissions(role: UserRole, allowedApps: string[]): string[] {
    const permissions = Object.entries(PERMISSION_MAP)
        .filter(([, roles]) => roles.includes(role))
        .map(([permission]) => permission);

    if (allowedApps.includes('resolve')) permissions.push('resolve.view');
    if (allowedApps.includes('dfmea')) permissions.push('dfmea.view');
    if (allowedApps.includes('pfmea')) permissions.push('pfmea.view');
    return permissions;
}

// Helper function to process authenticated user state logic
async function processAuthenticatedUser(fbUser: FirebaseUser): Promise<User | null> {
    const db = getFirestore();
    const userDocRef = doc(db, 'users', fbUser.uid);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
        console.warn('🚫 User not found in Firestore. Access denied.');
        return null;
    }

    const userData = userDoc.data();
    const isApproved = userData?.isApproved === true;
    const firestoreRole = userData?.role;

    if (!isApproved && firestoreRole !== 'autoGainAdmin') {
        console.warn('🚫 User not approved. Access denied.');
        return buildPendingUser(fbUser);
    }

    const tokenResult = await fbUser.getIdTokenResult();
    const role = (tokenResult.claims.role as UserRole) || firestoreRole || 'pending';
    const allowedApps = userData?.allowedApps || ['items'];
    const permissions = buildPermissions(role, allowedApps);

    return {
        uid: fbUser.uid,
        email: fbUser.email || '',
        displayName: fbUser.displayName || userData?.displayName || 'User',
        photoURL: fbUser.photoURL || undefined,
        role,
        permissions,
        allowedApps,
        organizationId: tokenResult.claims.organizationId as string | undefined,
        isApproved: true,
        createdAt: userData?.createdAt?.toDate() || new Date(),
        lastLogin: new Date(),
    };
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    // Check for redirect result on mount (for production auth flow)
    useEffect(() => {
        const handleRedirectResult = async () => {
            try {
                const result = await getRedirectResult(auth);
                if (result) {
                    console.log('✅ Redirect sign-in successful:', result.user.email);
                }
            } catch (err) {
                console.error('Redirect result error:', err);
            }
        };
        handleRedirectResult();
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
            setLoading(true);

            if (fbUser) {
                try {
                    const authenticatedUser = await processAuthenticatedUser(fbUser);
                    setUser(authenticatedUser);
                    if (authenticatedUser && authenticatedUser.isApproved) {
                        console.log(`✅ Magnus Resolve - User authenticated: ${authenticatedUser.email} (${authenticatedUser.role})`);
                    }
                } catch (error) {
                    console.error('Error checking user approval:', error);
                    setUser(null);
                }
            } else {
                setUser(null);
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const loginWithGoogle = async () => {
        try {
            console.log('🔄 Using redirect sign-in for authentication...');
            // signInWithRedirect avoids Cross-Origin-Opener-Policy (COOP) warnings
            // caused by Google's accounts.google.com COOP: same-origin header.
            // getRedirectResult() in the mount useEffect handles the result.
            await signInWithRedirect(auth, googleProvider);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : String(err);
            console.error('Sign in error:', err);
            throw new Error(message);
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : String(err);
            throw new Error(message);
        }
    };

    const login = async () => {
        throw new Error('Email/password login not implemented. Use Google Sign-In.');
    };

    const refreshUser = async () => {
        // Force refresh of auth state
        if (auth.currentUser) {
            await auth.currentUser.reload();
        }
    };

    const hasPermission = (permission: string): boolean => {
        if (!user) return false;
        if (!user.permissions) return false;
        return user.permissions.includes(permission);
    };

    const hasRole = (roles: UserRole[]): boolean => {
        if (!user) return false;
        return roles.includes(user.role);
    };

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            login,
            loginWithGoogle,
            logout,
            refreshUser,
            hasPermission,
            hasRole,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};
